import { createSearchParams, useNavigate } from "react-router-dom";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useState } from "react";
import { BaseLogo, getLogoOrgId } from "../../../../core/models/entities/BaseLogo";
import { Organization } from "../../../../core/models/entities/Organization";
import { LogoCommit } from "../../../../data/models/bodies/LogoCommit";
import { useOrderActionHandler } from "../../order-create/hooks/useOrderActionsHandler";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { useSuccessToast, withToasts } from "../../../components/Toast/Toast";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { getAllQuery } from "../../logo-editor/logo-editor.screen";
import { actAsCacheKey } from "../../../../contexts/actAsStore";
import { enviroment } from "../../../../enviroment";
import { markDetailHighResPending } from "../../../../data/repositories/logoDetailCache";
import { runCommitAndInitiateApproval } from "./initiateApprovalFlow";

export function useLogoActionHandler() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logoRepository, organizationRepository } = useRepositoriesContext();
  const queryClient = useQueryClient();
  const { handleLogoAddOrderLine, handleLogoAddOrderLines } =
    useOrderActionHandler();
  const cartHidden = useCartHidden();

  const [isLoading, setIsLoading] = useState(false);

  function handleGoToCreateNewLogo() {
    navigate("/logo/create");
  }

  function handleAddToCart(logo: BaseLogo, quantity: number) {
    if (cartHidden) {
      toast.error(t("logoList.cartNotAvailable"));
      return;
    }
    handleLogoAddOrderLine(logo, quantity, 0);
    useSuccessToast(t("logoList.addedToCart"));
  }

  function handleAddItemsToCart(logos: BaseLogo[], quantity: number) {
    if (cartHidden) {
      toast.error(t("logoList.cartNotAvailable"));
      return;
    }
    handleLogoAddOrderLines(logos, quantity, 0);
    useSuccessToast(t("logoList.addedToCart"));
  }

  function handleGoToEditLogo(logo: BaseLogo) {
    navigate({
      pathname: "/logo/create",
      search: createSearchParams({
        ["logo"]: "" + logo.id,
        ["organization"]: "" + getLogoOrgId(logo),
      }).toString(),
    });
  }

  function handleUpdateLogoColor(
    orgId: number,
    logoId: number,
    changeToColor: ChangeLogoColors
  ) {
    //console.log('Updating logo colors:', {
    //  orgId,
    //  logoId,
    //  colorChanges: changeToColor.colorChanges
    //});

    async function transaction() {
      return await logoRepository.changeColors(orgId, logoId, changeToColor);
    }

    return withToasts(
      transaction,
      t("logoList.updatingLogoColor"),
      t("logoList.logoColorUpdated"),
      t("logoList.logoColorUpdateFailed"),
      setIsLoading
    );
  }

  function handleChangeTitle(orgId: number, logoId: number, title: string) {
    async function transaction() {
      return await logoRepository.changeTitle(orgId, logoId, title);
    }

    return withToasts(
      transaction,
      t("logoList.updatingLogoTitle"),
      t("logoList.logoTitleUpdated"),
      t("logoList.logoTitleUpdateFailed"),
      setIsLoading
    );
  }

  function handleUpdateLogo(orgId: number, logo: BaseLogo) {
    async function transaction() {
      return await logoRepository.update(orgId, logo);
    }

    return withToasts(
      transaction,
      t("logoList.updatingLogo"),
      t("logoList.logoUpdated"),
      t("logoList.logoUpdateFailed"),
      setIsLoading
    );
  }

  function handleResizeLogo(orgId: number, logo: BaseLogo) {
    async function transaction() {
      return await logoRepository.resize(orgId, logo);
    }

    return withToasts(
      transaction,
      t("logoList.resizingLogo"),
      t("logoList.logoResized"),
      t("logoList.logoResizeFailed"),
      setIsLoading
    );
  }

  function handleCommitLogo(logo: BaseLogo, commit: LogoCommit) {
    async function transaction() {
      return await logoRepository.commit(logo, commit);
    }

    return withToasts(
      transaction,
      t("logoList.committingLogo"),
      t("logoList.logoCommitted"),
      t("logoList.logoCommitFailed"),
      setIsLoading
    );
  }

  function handleSaveThenSkipLogo(orgId: number, logo: BaseLogo) {
    async function transaction() {
      const updatedLogo = await logoRepository.update(orgId, logo);
      return await logoRepository.initiateApproval(updatedLogo, {
        skip: true,
        email: undefined,
      });
    }

    return withToasts(
      transaction,
      t("logoList.committingLogo"),
      t("logoList.logoCommitted"),
      t("logoList.logoCommitFailed"),
      setIsLoading
    );
  }

  type DeleteLogoOptions = {
    silent?: boolean;
  };

  async function handleDeleteLogo(logo: BaseLogo, options?: DeleteLogoOptions) {
    async function transaction() {
      return await logoRepository.delete(getLogoOrgId(logo), logo.id);
    }

    return withToasts(
      transaction,
      t("logoList.deletingLogo"),
      t("logoList.logoDeleted"),
      "",
      options?.silent ? undefined : setIsLoading
    );
  }

  function handleCreateLogo(
    file: File,
    customerId: Organization["id"],
    title: string
  ) {
    async function transaction() {
      const baseLogo = await logoRepository.createBaseLogo(customerId, title);
      const uploaded = await logoRepository.uploadFileToBaseLogo(
        file,
        customerId,
        baseLogo.id
      );
      // This path invalidates NOTHING today (unlike createImageLogo), so the
      // logo lists never learn about the new logo until their own staleTime
      // runs out. Mark every logo query stale without forcing refetches -
      // active screens pick it up on their next natural fetch. The detail
      // heal for the new logo itself is handled by useHealPendingHighRes.
      void queryClient.invalidateQueries({
        queryKey: ["logos"],
        refetchType: "none",
      });
      // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: seed the detail entry with the
      // UPLOAD response (mapped; it carries the real mm + low-res) so the
      // editor's heal subscription mounts on a warm cache - 0 extra GetLogo -
      // and the customer-list path (navigate to /logo/create?logo=) no longer
      // fires its own GetLogo on mount. The CREATE response (step 1) must
      // never seed: it has widthMm=heightMm=0 and no image.
      if (enviroment.logoHealPerMutationEnabled && uploaded?.id) {
        queryClient.setQueryData(
          ["logos", actAsCacheKey(), uploaded.id],
          uploaded
        );
      }
      return uploaded;
    }

    return withToasts(
      transaction,
      t("logoList.creatingLogo"),
      t("logoList.logoCreated"),
      t("logoList.logoCreateFailed"),
      setIsLoading
    );
  }

  function handleCreateTextLogo(
    value: string,
    color: string,
    heightMm: number,
    font: string,
    orgId: Organization["id"],
    customerId?: number,
    method?: string
  ): Promise<BaseLogo> {
    async function transaction() {
      return await logoRepository.createTextLogo(
        value,
        color,
        heightMm,
        font,
        orgId,
        customerId,
        method
      );
    }

    return withToasts(
      transaction,
      t("logoList.creatingTextLogo"),
      t("logoList.textLogoCreated"),
      t("logoList.textLogoCreateFailed"),
      setIsLoading
    );
  }

  function handleCreatePlaceholderNameLogo(
    color: string,
    heightMm: number,
    font: string,
    orgId: Organization["id"],
    customerId?: number,
    method?: string
  ): Promise<BaseLogo> {
    async function transaction() {
      return await logoRepository.createNameLogo(
        color,
        heightMm,
        font,
        orgId,
        customerId,
        method
      );
    }

    return withToasts(
      transaction,
      t("logoList.creatingNameLogo"),
      t("logoList.nameLogoCreated"),
      t("logoList.nameLogoCreateFailed"),
      setIsLoading
    );
  }

  function handleCreatePlaceholderNumberLogo(
    color: string,
    heightMm: number,
    font: string,
    method: string,
    orgId: Organization["id"],
    customerId?: number
  ): Promise<BaseLogo> {
    async function transaction() {
      return await logoRepository.createNumberLogo(
        color,
        heightMm,
        font,
        method,
        orgId,
        customerId
      );
    }

    return withToasts(
      transaction,
      t("logoList.creatingNumberset"),
      t("logoList.numbersetCreated"),
      t("logoList.numbersetCreateFailed"),
      setIsLoading
    );
  }

  function handlePlaceVerdict(logo: BaseLogo, verdict: Verdict) {
    async function transaction() {
      return await logoRepository.placeVerdict(logo, verdict);
    }

    // Button lock (flag approvalPatchEnabled): isLoading reaches the popup's
    // overlay and ApprovalVoting, so Approve/Reject cannot be clicked twice
    // while the PUT (prod p50 1.8 s, p95 3.1 s) is in flight. Flag off =
    // no isLoading, as today (toast only, buttons stay clickable).
    return withToasts(
      transaction,
      t("logoList.updatingApproval"),
      t("logoList.approvalSuccessful"),
      t("logoList.approvalFailed"),
      enviroment.approvalPatchEnabled ? setIsLoading : undefined
    );
  }

  function handleRejectAndDelete(logo: BaseLogo, note?: string) {
    async function transaction() {
      const placedVerdict = await logoRepository.placeVerdict(logo, {
        approve: false,
        rejectionNote: note ?? "",
      });
      return await logoRepository.delete(
        getLogoOrgId(placedVerdict),
        placedVerdict.id
      );
    }

    return withToasts(
      transaction,
      t("logoList.deleting"),
      t("logoList.deletionSuccessful"),
      t("logoList.deletionFailed"),
      setIsLoading
    );
  }

  // A server-side copy re-derives the artwork's natural size instead of keeping the
  // source logo's saved dimensions, so clones opened mis-sized in the editor (image
  // overflowing the rulers). Restore the source's saved size on the copy — but never
  // fail the copy flow over it: a fresh serial may not be resizable yet, and the
  // user can still resize manually.
  async function tryRestoreCopiedLogoSize(
    source: BaseLogo,
    copy: BaseLogo
  ): Promise<BaseLogo> {
    const hasValidSize =
      (source.widthMm ?? 0) > 0 && (source.heightMm ?? 0) > 0;
    const sizeDiffers =
      source.widthMm !== copy.widthMm || source.heightMm !== copy.heightMm;
    if (!hasValidSize || !sizeDiffers) return copy;

    try {
      return await logoRepository.resize(getLogoOrgId(copy), {
        ...copy,
        widthMm: source.widthMm,
        heightMm: source.heightMm,
      } as BaseLogo);
    } catch {
      return copy;
    }
  }

  function handleCopyLogo(logo: BaseLogo) {
    async function transaction() {
      const newLogo = await logoRepository.copy(getLogoOrgId(logo), logo.id);
      const restored = await tryRestoreCopiedLogoSize(logo, newLogo);
      // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: the copy/resize responses carry
      // the 180px processed blob as highResUrl, which the heal's equality
      // rule never classifies as pending - stamp the seeded entry so the
      // design editor's heal polls for the real render (max 3 GetLogo).
      // AFTER the size restore: its resize response re-seeds the entry and
      // would wipe a stamp set before it.
      if (enviroment.logoHealPerMutationEnabled) {
        markDetailHighResPending(queryClient, restored.id);
      }
      return restored;
    }

    return withToasts(
      transaction,
      t("logoList.copyingLogo"),
      t("logoList.copyingSuccessful"),
      t("logoList.copyingFailed"),
      setIsLoading
    );
  }

  function handleCopyThenGoToLogo(logo: BaseLogo) {
    async function transaction() {
      // Warm the editor's blocking org-list queries while the copy call runs:
      // the editor screen gates its ENTIRE render on all-customers/all-vendors
      // (useCustomerAndVendors) with a fullscreen spinner on a cold cache.
      // Firing the prefetches now lets the multi-second server-side copy pay
      // for them. Fail-silent cache warming with the exact key the screen uses
      // (getAllQuery) - same idiom as the login prefetch wave in App.tsx.
      organizationRepository.prefetchAllCustomers(getAllQuery);
      organizationRepository.prefetchAllVendors(getAllQuery);

      const newLogo = await logoRepository.copy(getLogoOrgId(logo), logo.id);

      // Detail cache key under the CURRENT act-as scope (["logos", actAs,
      // id] — same shared helper as LogoRepository.useLogo, which the editor
      // reads). Captured ONCE so the optimistic seed, the failed-restore heal
      // and the hi-res invalidation below all target the SAME entry even if
      // the admin switches identity while the ~6s background restore runs.
      const detailKey = ["logos", actAsCacheKey(), newLogo.id];

      // The size restore (tryRestoreCopiedLogoSize) used to be AWAITED here,
      // holding the user in the popup for the full resize round-trip (~6s
      // measured server-side) even though it is purely cosmetic size recovery.
      // Navigate as soon as the copy lands and run the restore in the
      // background instead. Two things make that safe:
      // 1) The editor freezes preselectedLogo/logoForm in useState at mount,
      //    so a resize finishing after navigation would never reach it anyway.
      //    We seed the detail cache with the SOURCE's saved dims up front (the
      //    exact values the resize will confirm), so the editor opens with the
      //    right size immediately.
      // 2) tryRestoreCopiedLogoSize never throws (own catch) and resize() runs
      //    seedAndMarkStale, so the cache converges on the server-confirmed
      //    state when the background call lands.
      const hasValidSize =
        (logo.widthMm ?? 0) > 0 && (logo.heightMm ?? 0) > 0;
      if (hasValidSize) {
        queryClient.setQueryData(detailKey, {
          ...newLogo,
          widthMm: logo.widthMm,
          heightMm: logo.heightMm,
        });
      }
      navigate({
        pathname: "/logo/create",
        search: createSearchParams({
          ["logo"]: "" + newLogo.id,
          ["organization"]: "" + getLogoOrgId(newLogo),
        }).toString(),
      });

      // Fire-and-forget by design (see above). Pass the server's copy (not the
      // optimistic seed) so the sizeDiffers check compares real server state.
      // If the resize FAILS (its own catch returns the unresized copy — a fresh
      // serial may not be resizable yet), heal the cache back to the server
      // truth: the optimistic source-dims seed must never outlive a failed
      // resize, or every cache reader would show a size the server never had.
      void tryRestoreCopiedLogoSize(logo, newLogo).then((restored) => {
        if (restored === newLogo) {
          queryClient.setQueryData(detailKey, newLogo);
        }
        // The copy/resize responses only carry the 180px images, and the
        // seeded detail cache never refetches on its own (refetchOnMount is
        // off globally) — so the popup/editor would show a blurry stretched
        // icon indefinitely. Once the size restore settles, force one real
        // GetLogo in the background: it live-renders the sharp hi-res (and
        // carries the replayed colors), and every cache reader glides over.
        // With REACT_APP_LOGO_HEAL_PER_MUTATION the editor's own heal takes over
        // from THAT response if it still lacks a genuine high-res (max 3 more
        // GetLogo) - no pending stamp here: the restore's re-seed would wipe
        // it, and this GetLogo already covers the common case.
        void queryClient.invalidateQueries({
          queryKey: detailKey,
          refetchType: "all",
        });
      });
    }

    return withToasts(
      transaction,
      t("logoList.copyingLogo"),
      t("logoList.copyingSuccessful"),
      t("logoList.copyingFailed"),
      setIsLoading
    );
  }

  function handleInitiateApproval(logo: BaseLogo, approval: InitiateApproval) {
    async function transaction() {
      const approvedLogo = await logoRepository.initiateApproval(
        logo,
        approval
      );
      // return await logoRepository.placeVerdict(approvedLogo, { approve: true })
    }

    return withToasts(
      transaction,
      t("logoList.updatingApproval"),
      t("logoList.approvalSuccessful"),
      t("logoList.approvalFailed"),
      setIsLoading
    );
  }

  function handleSkipApproval(logo: BaseLogo) {
    async function transaction() {
      return await logoRepository.initiateApproval(logo, {
        skip: true,
        email: undefined,
      });
    }

    return withToasts(
      transaction,
      t("logoList.skippingApproval"),
      t("logoList.approvalSkipSuccessful"),
      t("logoList.approvalSkipFailed"),
      setIsLoading
    );
  }

  function handleSendApproval(logo: BaseLogo, email: string) {
    async function transaction() {
      return await logoRepository.sendApproval(logo, email);
    }

    return withToasts(
      transaction,
      t("logoList.sendingEmail"),
      t("logoList.emailSendSuccessful"),
      t("logoList.emailSendFailed"),
      setIsLoading
    );
  }

  function handleSendApprovalWithEndCustomerPending(
    logo: BaseLogo,
    email: string
  ) {
    async function transaction() {
      // Use conditional approval that preserves existing approval statuses
      return await logoRepository.sendApprovalConditional(logo, email);
    }

    return withToasts(
      transaction,
      t("logoList.sendingApproval"),
      t("logoList.approvalSendSuccessful"),
      t("logoList.approvalSendFailed"),
      setIsLoading
    );
  }

  function handleCommitAndInitiateApproval(
    logo: BaseLogo,
    commit: LogoCommit,
    approval: InitiateApproval
  ) {
    async function transaction() {
      // Flag approvalListFlowFixEnabled: skip the dead commit call (see
      // initiateApprovalFlow). Flag off = commit first, as today.
      await runCommitAndInitiateApproval(
        logoRepository,
        logo,
        commit,
        approval,
        enviroment.approvalListFlowFixEnabled
      );
      // return await logoRepository.placeVerdict(approvedLogo, { approve: true });
    }

    return withToasts(
      transaction,
      t("logoList.updatingApproval"),
      t("logoList.approvalSuccessful"),
      t("logoList.approvalFailed"),
      setIsLoading
    );
  }

  function handleSkipAddToCart(logo: BaseLogo, quantity: number) {
    if (cartHidden) {
      toast.error(t("logoList.cartNotAvailable"));
      return Promise.reject(new Error("Cart hidden"));
    }
    async function transaction() {
      return await logoRepository
        .initiateApproval(logo, {
          skip: true,
          email: undefined,
        })
        .then((updatedLogo) => {
          handleAddToCart(updatedLogo, quantity);
          return updatedLogo;
        });
    }

    return withToasts(
      transaction,
      t("logoList.skippingApprovalAddingToCart"),
      t("logoList.approvalSkippedAddedToCart"),
      t("logoList.approvalSkipFailed"),
      setIsLoading
    );
  }

  return {
    isLoading,
    handleGoToCreateNewLogo,
    handleAddToCart,
    handleAddItemsToCart,
    handleGoToEditLogo,
    handleDeleteLogo,
    handleChangeTitle,
    handlePlaceVerdict,
    handleRejectAndDelete,
    handleCreateLogo,
    handleCreateTextLogo,
    handleCreatePlaceholderNameLogo,
    handleCreatePlaceholderNumberLogo,
    handleUpdateLogoColor,
    handleUpdateLogo,
    handleResizeLogo,
    handleCopyThenGoToLogo,
    handleCopyLogo,
    handleCommitLogo,
    handleSaveThenSkipLogo,
    handleInitiateApproval,
    handleCommitAndInitiateApproval,
    handleSkipApproval,
    handleSendApproval,
    handleSendApprovalWithEndCustomerPending,
    handleSkipAddToCart,
  };
}

export interface ChangeLogoColors {
  colorChanges: ColorChange[];
}

export interface InitiateApproval {
  skip?: boolean;
  email?: string;
}

export interface ColorChange {
  from: string;
  to: string;
}
